import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Search, Loader2 } from "lucide-react";
import { algoliasearch } from "algoliasearch";
import type { SearchClient } from "algoliasearch";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNotification } from "@/context/NotificationContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getEmployeeSearchConfig } from "@/api/getEmployeeSearchConfig/getEmployeeSearchConfigApi";
import {
  EmployeeSearchConfig,
  EmployeeSearchHit,
} from "@/api/getEmployeeSearchConfig/types";
import { shareResourceToEmployees } from "@/api/shareResourceToEmployees/shareResourceToEmployeesApi";
import { unshareResourceFromEmployees } from "@/api/unshareResourceFromEmployees/unshareResourceFromEmployeesApi";
import { checkShareStatus } from "@/api/checkShareStatus/checkShareStatusApi";

interface ShareToEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceUuid: string;
  resourceTitle: string;
}

interface EmployeeWithSelection extends EmployeeSearchHit {
  isSelected: boolean;
  isShared: boolean;
}

export default function ShareToEmployeeModal({
  isOpen,
  onClose,
  resourceUuid,
  resourceTitle,
}: ShareToEmployeeModalProps) {
  const { showNotification } = useNotification();
  const [employees, setEmployees] = useState<EmployeeWithSelection[]>([]);
  // Track employees we've discovered were already shared (accumulates across searches)
  const [initialSharedUuids, setInitialSharedUuids] = useState<Set<string>>(
    new Set(),
  );
  // Track user's current selections (separate from share status)
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const HITS_PER_PAGE = 20;

  // Algolia state
  const [searchConfig, setSearchConfig] = useState<EmployeeSearchConfig | null>(
    null,
  );
  const searchClientRef = useRef<SearchClient | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  // Batch-check share status for a list of employee UUIDs
  const batchCheckShareStatus = useCallback(
    async (employeeUuids: string[]): Promise<Set<string>> => {
      if (employeeUuids.length === 0) return new Set();

      try {
        const response = await checkShareStatus(resourceUuid, employeeUuids);
        return new Set(response.sharedEmployeeUuids);
      } catch (error) {
        console.error("Error checking share status:", error);
        return new Set();
      }
    },
    [resourceUuid],
  );

  // Fetch search config when modal opens
  useEffect(() => {
    if (isOpen) {
      initializeModal();
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, resourceUuid]);

  // Search when search term changes (reset to page 0)
  useEffect(() => {
    if (!isOpen || !searchConfig || !searchClientRef.current) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPage(0);
      setHasMore(true);
      searchEmployees(searchTerm, 0, true);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, searchConfig]);

  const initializeModal = async () => {
    try {
      // Only fetch Algolia config - no upfront share status fetch!
      const config = await getEmployeeSearchConfig();

      setSearchConfig(config);

      // Initialize Algolia client with secured key
      searchClientRef.current = algoliasearch(
        config.applicationId,
        config.securedApiKey,
      );

      // Do initial search (empty query returns default results)
      setPage(0);
      setHasMore(true);
      await searchEmployees("", 0, true);
    } catch (error) {
      console.error("Error initializing modal:", error);
      showNotification(
        "Error",
        "Failed to initialize sharing. Please try again.",
        "error",
      );
      setInitialLoading(false);
    }
  };

  const searchEmployees = async (
    query: string,
    pageNum: number = 0,
    replace: boolean = false,
  ) => {
    if (!searchClientRef.current || !searchConfig) return;

    try {
      // 1. Search Algolia
      const result = await searchClientRef.current.searchSingleIndex({
        indexName: searchConfig.index,
        searchParams: {
          query,
          hitsPerPage: HITS_PER_PAGE,
          page: pageNum,
        },
      });

      const hits = result.hits as EmployeeSearchHit[];

      // Check if there are more results
      const totalHits = result.nbHits || 0;
      const loadedCount = (pageNum + 1) * HITS_PER_PAGE;
      setHasMore(loadedCount < totalHits);

      // 2. Show employees IMMEDIATELY (without share status yet)
      const newEmployees: EmployeeWithSelection[] = hits.map((hit) => ({
        ...hit,
        isShared: false, // Will be updated when batch-check returns
        isSelected: selectedUuids.has(hit.uuid), // Preserve existing selections
      }));

      if (replace) {
        setEmployees((prev) => {
          const prevSelectedUuids = new Set(
            prev.filter((e) => e.isSelected).map((e) => e.uuid),
          );
          return newEmployees.map((emp) => ({
            ...emp,
            isSelected:
              prevSelectedUuids.has(emp.uuid) || selectedUuids.has(emp.uuid),
          }));
        });
      } else {
        setEmployees((prev) => {
          const existingUuids = new Set(prev.map((e) => e.uuid));
          const prevSelectedUuids = new Set(
            prev.filter((e) => e.isSelected).map((e) => e.uuid),
          );
          const uniqueNewEmployees = newEmployees
            .filter((emp) => !existingUuids.has(emp.uuid))
            .map((emp) => ({
              ...emp,
              isSelected:
                prevSelectedUuids.has(emp.uuid) || selectedUuids.has(emp.uuid),
            }));
          return [...prev, ...uniqueNewEmployees];
        });
      }

      // Turn off initial loading as soon as we have employees to show
      setInitialLoading(false);

      // 3. Now fetch share status in background and update
      const hitUuids = hits.map((hit) => hit.uuid);
      const sharedUuidsInBatch = await batchCheckShareStatus(hitUuids);

      // 4. Accumulate discovered shared employees
      setInitialSharedUuids((prev) => {
        const newSet = new Set(prev);
        sharedUuidsInBatch.forEach((uuid) => newSet.add(uuid));
        return newSet;
      });

      // 5. Auto-select newly discovered shared employees
      setSelectedUuids((prev) => {
        const newSet = new Set(prev);
        sharedUuidsInBatch.forEach((uuid) => newSet.add(uuid));
        return newSet;
      });

      // 6. Update employees with share status
      setEmployees((prev) =>
        prev.map((emp) => ({
          ...emp,
          isShared: sharedUuidsInBatch.has(emp.uuid) || emp.isShared,
          isSelected: sharedUuidsInBatch.has(emp.uuid) || emp.isSelected,
        })),
      );
    } catch (error) {
      console.error("Error searching employees:", error);
    }
  };

  const fetchMoreEmployees = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    searchEmployees(searchTerm, nextPage, false);
  };

  const handleClose = () => {
    setSearchTerm("");
    setEmployees([]);
    setInitialSharedUuids(new Set());
    setSelectedUuids(new Set());
    setSearchConfig(null);
    setPage(0);
    setHasMore(true);
    setInitialLoading(true);
    searchClientRef.current = null;
    onClose();
  };

  const toggleEmployee = (uuid: string) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.uuid === uuid ? { ...emp, isSelected: !emp.isSelected } : emp,
      ),
    );
    setSelectedUuids((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setEmployees((prev) => prev.map((emp) => ({ ...emp, isSelected: true })));
    setSelectedUuids((prev) => {
      const newSet = new Set(prev);
      employees.forEach((emp) => newSet.add(emp.uuid));
      return newSet;
    });
  };

  const handleDeselectAll = () => {
    setEmployees((prev) => prev.map((emp) => ({ ...emp, isSelected: false })));
    setSelectedUuids((prev) => {
      const newSet = new Set(prev);
      employees.forEach((emp) => newSet.delete(emp.uuid));
      return newSet;
    });
  };

  // Calculate changes for button text and state
  const { hasChanges, toShareCount, toUnshareCount } = useMemo(() => {
    const currentSelectedArray = employees
      .filter((e) => e.isSelected)
      .map((e) => e.uuid);
    const currentSelected = new Set(currentSelectedArray);
    const initialSharedArray = Array.from(initialSharedUuids);

    const toShare = currentSelectedArray.filter(
      (uuid) => !initialSharedUuids.has(uuid),
    );
    const toUnshare = initialSharedArray.filter(
      (uuid) => !currentSelected.has(uuid),
    );

    return {
      hasChanges: toShare.length > 0 || toUnshare.length > 0,
      toShareCount: toShare.length,
      toUnshareCount: toUnshare.length,
    };
  }, [employees, initialSharedUuids]);

  // Generate button text based on action
  const getButtonText = () => {
    if (saving) return "Sharing...";
    if (toShareCount > 0 && toUnshareCount > 0) {
      return `Share (${toShareCount}) & Remove (${toUnshareCount})`;
    }
    if (toShareCount > 0) {
      return toShareCount === 1 ? "Share" : `Share with ${toShareCount}`;
    }
    if (toUnshareCount > 0) {
      return toUnshareCount === 1 ? "Remove" : `Remove ${toUnshareCount}`;
    }
    return "Share";
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentSelected = new Set(
        employees.filter((e) => e.isSelected).map((e) => e.uuid),
      );

      // Calculate what changed
      const toShare = Array.from(currentSelected).filter(
        (uuid) => !initialSharedUuids.has(uuid),
      );
      const toUnshare = Array.from(initialSharedUuids).filter(
        (uuid) => !currentSelected.has(uuid),
      );

      const promises = [];

      if (toShare.length > 0) {
        promises.push(
          shareResourceToEmployees({
            resourceUuid,
            employeeUuids: toShare,
          }),
        );
      }

      if (toUnshare.length > 0) {
        promises.push(
          unshareResourceFromEmployees({
            resourceUuid,
            employeeUuids: toUnshare,
          }),
        );
      }

      await Promise.all(promises);

      // Build a natural success message
      let message = "";
      if (toShare.length > 0 && toUnshare.length > 0) {
        message = `Shared with ${toShare.length} and removed ${toUnshare.length}`;
      } else if (toShare.length > 0) {
        message =
          toShare.length === 1
            ? "Successfully shared with 1 employee"
            : `Successfully shared with ${toShare.length} employees`;
      } else if (toUnshare.length > 0) {
        message =
          toUnshare.length === 1
            ? "Removed sharing from 1 employee"
            : `Removed sharing from ${toUnshare.length} employees`;
      }

      showNotification("Success", message, "success");

      handleClose();
    } catch (error) {
      console.error("Error saving shares:", error);
      showNotification(
        "Error",
        "Failed to update sharing. Please try again.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden dark:bg-slate-900 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-slate-700">
          <DialogTitle className="text-sm font-medium text-gray-900 dark:text-white">
            Share Resource
          </DialogTitle>
        </div>

        {/* Search */}
        <div className="px-3 py-2.5 border-b border-gray-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              className="h-8 pl-7 pr-3 text-[13px] bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 focus:border-gray-300 dark:focus:border-slate-600"
              disabled={initialLoading}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search employees..."
              value={searchTerm}
            />
          </div>
          {/* Select/Deselect links */}
          {!initialLoading && employees.length > 0 && (
            <div className="flex gap-3 mt-2 text-[12px]">
              <button
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                onClick={handleSelectAll}
                type="button"
              >
                Select all
              </button>
              <button
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                onClick={handleDeselectAll}
                type="button"
              >
                Deselect all
              </button>
            </div>
          )}
        </div>

        {/* Employee List */}
        <div
          className="overflow-y-auto min-h-[200px] max-h-[280px]"
          id="employee-scroll-container"
          ref={scrollableRef}
        >
          {initialLoading ? (
            <div className="flex flex-col items-center justify-center h-[200px]">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
                Loading...
              </span>
            </div>
          ) : employees.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-[13px] text-gray-500 dark:text-gray-400">
              {searchTerm ? "No employees found" : "No employees available"}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={employees.length}
              endMessage={
                employees.length > 0 ? (
                  <div className="text-center py-2 text-[12px] text-gray-400 dark:text-gray-500">
                    End of list
                  </div>
                ) : null
              }
              hasMore={hasMore}
              loader={
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                </div>
              }
              next={fetchMoreEmployees}
              scrollableTarget="employee-scroll-container"
            >
              <div>
                {employees.map((employee) => (
                  <label
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                    key={employee.uuid}
                  >
                    <Checkbox
                      checked={employee.isSelected}
                      onCheckedChange={() => toggleEmployee(employee.uuid)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
                        {employee.firstName} {employee.lastName}
                        {employee.businessRoleLabel && (
                          <span className="text-gray-500 dark:text-gray-400 font-normal ml-1.5">
                            · {employee.businessRoleLabel}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
                        {employee.email}
                      </div>
                      <div className="text-[12px] text-gray-400 dark:text-gray-500 truncate">
                        {employee.companyName}
                      </div>
                    </div>
                    {employee.isShared && (
                      <span className="text-[11px] text-green-600 dark:text-green-400 whitespace-nowrap">
                        Shared
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-3 py-2.5 border-t border-gray-200 dark:border-slate-700">
          <button
            className="flex-1 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            disabled={saving}
            onClick={handleClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`flex-1 px-2.5 py-1.5 text-[13px] font-medium text-white transition-colors flex items-center justify-center ${
              hasChanges && !saving
                ? "bg-primary hover:bg-primary/90"
                : "bg-gray-300 dark:bg-slate-600 cursor-not-allowed"
            }`}
            disabled={!hasChanges || saving}
            onClick={handleSave}
            type="button"
          >
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {getButtonText()}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
